import type {BrandColor, BrandFont, BrandKitWithAssets, BrandLogo} from '@Components/mystuff-brands/brand-kits.types';
import {BrandColorType, BrandImageType, FontType} from '@Components/mystuff-brands/brand-kits.types';
import {repoImageThumbURL} from '@Libraries/graphic-item-image-library';
import {hextToHexString} from '@Utils/color.util';

export interface BrandKitDisplayRow {
  brandKitWithAssets: BrandKitWithAssets;
  displayFontFamily: string | undefined;
}

const brandKitNameCollator = new Intl.Collator(undefined, {sensitivity: 'base', numeric: true});

export const getBrandKitDisplayRowsPinnedFirst = (brandKits: BrandKitWithAssets[], pinnedBrandKitId: string | null): BrandKitDisplayRow[] => {
  return [...brandKits]
    .sort((firstBrandKit, secondBrandKit) => {
      if (firstBrandKit.brandKit.brandId === pinnedBrandKitId) {
        return -1;
      }
      if (secondBrandKit.brandKit.brandId === pinnedBrandKitId) {
        return 1;
      }

      return brandKitNameCollator.compare(firstBrandKit.brandKit.name, secondBrandKit.brandKit.name);
    })
    .map((brandKitWithAssets) => {
      return {brandKitWithAssets, displayFontFamily: getBrandKitDisplayFontFamily(brandKitWithAssets.fonts)};
    });
};

export const getBrandKitLogoURL = (brandLogos: BrandLogo[]): string | undefined => {
  if (brandLogos.length === 0) {
    return undefined;
  }

  const primaryLogo = brandLogos.find((brandLogo) => {
    return brandLogo.brandImageType === BrandImageType.PRIMARY_LOGO;
  });

  const logoToUse = primaryLogo ?? brandLogos[0];

  return repoImageThumbURL(logoToUse.filename, logoToUse.ext);
};

export const getOrderedBrandColorHexes = (brandColors: BrandColor[], limit: number): string[] => {
  const orderedColors: BrandColor[] = [];

  const primaryColor = brandColors.find((brandColor) => {
    return brandColor.type === BrandColorType.PRIMARY;
  });

  if (primaryColor) {
    orderedColors.push(primaryColor);
  }

  const secondaryColor = brandColors.find((brandColor) => {
    return brandColor.type === BrandColorType.SECONDARY;
  });

  if (secondaryColor) {
    orderedColors.push(secondaryColor);
  }

  const otherColors = brandColors.filter((brandColor) => {
    return brandColor.type === BrandColorType.OTHER;
  });

  orderedColors.push(...otherColors);

  return orderedColors.slice(0, limit).map((brandColor) => {
    return hextToHexString(brandColor.hexCode);
  });
};

export const getBrandKitDisplayFontFamily = (brandFonts: BrandFont[]): string | undefined => {
  const headingFont = brandFonts.find((brandFont) => {
    return brandFont.fontType === FontType.HEADING;
  });

  const bodyFont = brandFonts.find((brandFont) => {
    return brandFont.fontType === FontType.BODY;
  });

  return (headingFont ?? bodyFont ?? brandFonts[0])?.fontFamily;
};
